// ./src/utils/ticketedStream.ts
//
// ONE way to hold a ticketed host/crew SSE stream (`/comments/stream?ticket=…`) open for as long as a screen needs it.
//
// ⚠⚠ WHY (owner, 2026-10-07: "the real-time messaging is not working — user has to fully reload page, there is no typing
// indicator … I remember it was working at some point"). A stream ticket lives 30 minutes. Each opener minted ONE when it
// mounted, and the browser's EventSource reconnects after any drop (the API restarting, a laptop waking, a network blip)
// with the SAME url. Past 30 minutes that ticket no longer verifies, and the server answered with a silent idle stream:
// nothing arrived, "is typing" pings carrying the old ticket were dropped too, and nothing said why — until a full
// reload minted a new one. It "worked" for half an hour after every page load.
// Now:
//   • the server says `{"type":"ticket.invalid"}` on such a stream → this mints a fresh ticket and reopens;
//   • a connection the browser has given up on (`readyState === CLOSED`) is reopened the same way, with backoff;
//   • the ticket is re-minted every `refreshMs` (default 20 min, under the 30-min life) so the CURRENT ticket — the one
//     typing pings use, and the next open — is always valid. An OPEN connection is never cut for this: the server
//     checks a ticket only when a connection opens.
//   • `onOpen(isReconnect)` lets the caller refetch what it may have missed (the crew stream does not replay).
// ⓘ No ticket (signed out, no access) → it retries quietly with backoff; the screen just has no live push.

export interface TicketedStreamOptions {
  /** The API root (`appEnv.rootApiUrl`). */
  base: string;
  /** The stream path, e.g. `/comments/stream`. */
  path: string;
  /** Mint a ticket for this screen's event — null when none can be had. */
  mintTicket: () => Promise<string | null>;
  /** Every ticket in use, as it changes (null when stopped) — e.g. for typing pings. */
  onTicket?: (ticket: string | null) => void;
  /** The stream opened; `isReconnect` is true for every open after the first. */
  onOpen?: (isReconnect: boolean) => void;
  /** A parsed data frame (never `ticket.invalid`, which this handles). */
  onFrame: (frame: any) => void;
  /** How often the ticket is re-minted (ms). Default 20 minutes. */
  refreshMs?: number;
}

const BACKOFF_MS = [1000, 3000, 10000, 30000];

/** Open a ticketed stream; returns a stop function. Safe to call where EventSource does not exist (it does nothing). */
export const openTicketedStream = (opts: TicketedStreamOptions): (() => void) => {
  if (typeof EventSource === "undefined" || !opts.base) {
    return () => undefined;
  }

  let stopped = false;
  let es: EventSource | null = null;
  let ticket: string | null = null;
  let openedOnce = false;
  let failures = 0;
  let retryTimer: ReturnType<typeof setTimeout> | null = null;

  const setTicket = (next: string | null): void => {
    ticket = next;
    opts.onTicket?.(next);
  };

  const closeCurrent = (): void => {
    if (es) {
      es.onopen = null;
      es.onmessage = null;
      es.onerror = null;
      es.close();
      es = null;
    }
  };

  const scheduleReopen = (): void => {
    if (stopped || retryTimer) {
      return;
    }

    const delay = BACKOFF_MS[Math.min(failures, BACKOFF_MS.length - 1)];
    failures += 1;
    retryTimer = setTimeout(() => {
      retryTimer = null;
      void open(true);
    }, delay);
  };

  // `fresh`: mint a new ticket first (a reopen after the old one was refused or the browser gave up).
  const open = async (fresh: boolean): Promise<void> => {
    closeCurrent();
    if (fresh || !ticket) {
      const minted = await opts.mintTicket().catch(() => null);
      if (stopped) {
        return;
      }
      setTicket(minted);
    }

    if (!ticket) {
      scheduleReopen();

      return;
    }

    const source = new EventSource(`${opts.base}${opts.path}?ticket=${encodeURIComponent(ticket)}`);
    es = source;

    source.onopen = () => {
      failures = 0;
      opts.onOpen?.(openedOnce);
      openedOnce = true;
    };

    source.onmessage = (evt) => {
      let frame: any = null;
      try {
        frame = JSON.parse(evt.data);
      } catch {
        return;
      }

      if (frame?.type === "ticket.invalid") {
        // The server no longer accepts this ticket (expired): fresh ticket, fresh connection.
        closeCurrent();
        scheduleReopen();

        return;
      }

      opts.onFrame(frame);
    };

    source.onerror = () => {
      // The browser retries on its own while CONNECTING; CLOSED means it has given up (a non-200, a fatal error).
      if (source.readyState === EventSource.CLOSED) {
        closeCurrent();
        scheduleReopen();
      }
    };
  };

  // Keep the CURRENT ticket valid — for typing pings and the next open — without cutting the open connection.
  const refreshTimer = setInterval(() => {
    void opts
      .mintTicket()
      .then((minted) => {
        if (!stopped && minted) {
          setTicket(minted);
        }
      })
      .catch(() => undefined);
  }, opts.refreshMs ?? 20 * 60 * 1000);

  void open(true);

  return () => {
    stopped = true;
    clearInterval(refreshTimer);
    if (retryTimer) {
      clearTimeout(retryTimer);
    }
    closeCurrent();
    setTicket(null);
  };
};
